import { useCallback, useEffect } from "react";

const REPORT_PATH_REGEX = /^\/(?:web\/)?report\/([0-9a-fA-F]{24})$/;

/**
 * Extract a report id from a deeplink pathname like
 * /report/<24-hex id> (or /web/report/<id> when served under /web).
 */
export const parseReportIdFromPath = (
  pathname: string = window.location.pathname
): string | null => {
  const match = pathname.match(REPORT_PATH_REGEX);
  return match ? match[1] : null;
};

/** Base path prefix ("/web" or "") the app is currently served under. */
const basePath = (): string =>
  window.location.pathname === "/web" ||
  window.location.pathname.startsWith("/web/")
    ? "/web"
    : "";

export const buildReportShareUrl = (reportId: string): string =>
  `${window.location.origin}/report/${reportId}`;

/**
 * Keeps the browser URL in sync with the selected report so reports are
 * deep-linkable: opening/closing the details panel pushes /report/<id> / the
 * root path, and back/forward navigation reopens/closes the panel.
 */
export function useReportUrlSync(
  setSelectedReportId: (id: string | null) => void
) {
  useEffect(() => {
    const handlePopState = () => {
      setSelectedReportId(parseReportIdFromPath());
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [setSelectedReportId]);

  const openReportUrl = useCallback((reportId: string) => {
    if (parseReportIdFromPath() !== reportId) {
      window.history.pushState(null, "", `${basePath()}/report/${reportId}`);
    }
  }, []);

  const closeReportUrl = useCallback(() => {
    if (parseReportIdFromPath() !== null) {
      window.history.pushState(null, "", `${basePath()}/`);
    }
  }, []);

  const resetReportUrl = useCallback(() => {
    if (parseReportIdFromPath() !== null) {
      window.history.replaceState(null, "", `${basePath()}/`);
    }
  }, []);

  return { openReportUrl, closeReportUrl, resetReportUrl };
}
